<template>
  <div class="page-max-width">
    <QPageContainer>
      <router-view />
    </QPageContainer>

    <ReviewDemandModal v-if="reviewDemandModalOpened" v-model:opened="reviewDemandModalOpened" :review="review" @on-submit-review="onSubmitReview" />
  </div>
</template>

<script>
  import { EventManager } from "@core/managers/event.manager";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { RecruitmentsService, SurveyService } from "@core/services";
  import { StatusBar } from "@core/plugins/mobile";
  import { ETAB_ROUTES, ETAB_EVENTS } from "@etab/config";
  import {
    ACTION_RECRUITMENTS_GET_IN_PROGRESS,
    ACTION_RECRUITMENTS_GET_UPCOMING,
    ACTION_RECRUITMENTS_SOFT_REMOVE,
    ACTION_SURVEY_GET_AMOUNT,
  } from "@etab/stores/types";
  import { ReviewDemandModel, IReview } from "@core/models";
  import { DEMAND_TYPES } from "@core/constants";
  import { useRecruitmentsStore } from "@etab/stores/recruitments";
  import { useSurveyStore } from "@etab/stores/survey";
  import { mapStores } from "pinia";

  import ReviewDemandModal from "@etab/components/survey/ReviewDemand.modal.vue";

  export default {
    components: {
      ReviewDemandModal,
    },
    data() {
      return {
        review: new ReviewDemandModel(),
        reviewDemandModalOpened: false,
      };
    },
    computed: {
      ...mapStores(useSurveyStore, useRecruitmentsStore),
    },
    created() {
      StatusBar.setDefault();

      this.events = [
        { name: ETAB_EVENTS.RECRUITMENT.CANCEL, action: this.cancelDemand },
        { name: ETAB_EVENTS.RECRUITMENT.CDI_REVIEW, action: this.openReview },
        { name: ETAB_EVENTS.RECRUITMENT.GO_TO_CREATION, action: this.goToCreation },
        { name: ETAB_EVENTS.RECRUITMENT.GO_TO_DETAILS, action: this.goToDetail },
        { name: ETAB_EVENTS.RECRUITMENT.GO_TO_PERMANENT_FEEDBACK, action: this.goToPermanentFeedback },
        { name: ETAB_EVENTS.RECRUITMENT.REFRESH, action: this.refreshRecruitments },
        { name: ETAB_EVENTS.RECRUITMENT.RESTART, action: this.restartDemand },
      ];

      this.registerEvents();
    },
    beforeUnmount() {
      this.unregisterEvents();
    },
    mounted() {
      this.$_stopGlobalLoading();
      this.getSurveysAmount();
    },
    methods: {
      registerEvents() {
        EventManager.registerAllEvents(this.events);
      },
      unregisterEvents() {
        EventManager.unregisterAllEvents(this.events);
      },
      async getSurveysAmount() {
        try {
          await this.surveyStore[ACTION_SURVEY_GET_AMOUNT]();
        } catch (error) {
          // RG: fail silently, it's not so important
        }
      },
      async refreshRecruitments() {
        try {
          this.$_startGlobalLoading();
          if (this.$route.name === ETAB_ROUTES.dashboard.tabs.upcoming.name) {
            await this.recruitmentsStore[ACTION_RECRUITMENTS_GET_UPCOMING]();
          }
          if (this.$route.name === ETAB_ROUTES.dashboard.tabs.inProgress.name) {
            await this.recruitmentsStore[ACTION_RECRUITMENTS_GET_IN_PROGRESS]();
          }
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@REFRESH_DMD_ERROR" });
          throw error;
        } finally {
          this.$_stopGlobalLoading(); // in case there's a loading
        }
      },
      removeRecruitmentFromView({ id }) {
        // we remove the item from the view, waiting for the auto refresh to confirm it's effectively removed from the server
        this.recruitmentsStore[ACTION_RECRUITMENTS_SOFT_REMOVE](id);
      },
      goToCreation() {
        this.$router.push({ name: ETAB_ROUTES.creation.root.name }).catch((suppressedError) => {});
      },
      goToPermanentFeedback({ recruitment, hasRecruited }) {
        const demandId = recruitment.getDemandId();
        const type = recruitment.getType();
        const demandType = type.toLowerCase();
        const routeName = hasRecruited
          ? ETAB_ROUTES.dashboard.recruitment.permanent.feedback.recruited.name
          : ETAB_ROUTES.dashboard.recruitment.permanent.feedback.notRecruited.name;

        this.$router
          .push({
            name: routeName,
            params: { demandId, demandType, hasRecruited },
          })
          .catch((suppressedError) => {});
      },
      goToDetail({ recruitment }) {
        const demandId = recruitment.getDemandId();
        const missionId = recruitment.getMissionId();
        const type = recruitment.getType();
        const demandType = type.toLowerCase();

        switch (type) {
          case DEMAND_TYPES.CDI:
          case DEMAND_TYPES.SEASON:
            if (recruitment.isUnlocked()) {
              this.$router
                .push({
                  name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.root.name,
                  params: { demandId, demandType },
                })
                .catch((suppressedError) => {});
            } else {
              this.$router
                .push({
                  name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.locked.name,
                  params: { demandId, demandType },
                })
                .catch((suppressedError) => {});
            }
            break;
          case DEMAND_TYPES.AE:
          case DEMAND_TYPES.INTERIM:
            this.$router
              .push({
                name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.root.name,
                params: { demandId: missionId, demandType },
              })
              .catch((suppressedError) => {});
            break;
          default:
            break;
        }
      },
      async cancelDemand({ recruitment }) {
        try {
          this.$_startGlobalLoading();

          if (recruitment.isAE() || recruitment.isInterim()) {
            await RecruitmentsService.cancelAEDemand({ missionId: recruitment.getMissionId() });
          } else if (recruitment.isPermanent()) {
            await RecruitmentsService.cancelPermanentDemand({ demandId: recruitment.getDemandId() });
          }
          this.removeRecruitmentFromView({ id: recruitment.getMissionId() });
          this.refreshRecruitments();
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async restartDemand({ recruitment }) {
        // RG: Recruitment restart - we do not restart recruitments in progress
        if (recruitment.hasStarted()) {
          // if mission has started, we cancel it
          await this.cancelDemand({ recruitment });
          // then go to creation page
          this.$router.push({ name: ETAB_ROUTES.creation.home.name }).catch((suppressedError) => {});
        } else {
          // RG: Recruitment restart - if mission hasn't started yet, we can restart the demand
          try {
            this.$_startGlobalLoading();
            await RecruitmentsService.restartDemand({ recruitment });
            this.removeRecruitmentFromView({ id: recruitment.getDemandId() });
            NotificationsManager.success({ message: "SUCCESS.@DEMAND_RESTARTED" });
            await this.refreshRecruitments();
            this.$_stopGlobalLoading();
          } catch (error) {
            this.$_stopGlobalLoading();
            NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
            throw error;
          }
        }
      },
      openReview({ recruitment }) {
        this.review = new ReviewDemandModel({
          [IReview.demandId]: recruitment.getDemandId(),
          [IReview.demandType]: recruitment.getType(),
          [IReview.qualification]: recruitment.getQualificationLabel(),
          [IReview.recruited]: false,
        });
        this.reviewDemandModalOpened = true;
      },
      async onSubmitReview({ review, comment }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.postDemandReview({ demandId: review.getDemandId(), comment });
          this.removeRecruitmentFromView({ id: review.getDemandId() });
          this.refreshRecruitments();
          const message = this.$t("ETAB.PAGES.RECRUITMENT.FEEDBACK_FORM.@CLOSE_DEMAND_SUCCESS");
          NotificationsManager.success({ message });
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          this.reviewDemandModalOpened = false;
          this.$_stopGlobalLoading();
        }
      },
    },
  };
</script>
